import React, { useState } from "react";
import CardLoader from "./CardLoader";
import { IoTrendingUp, IoTrendingDown, IoInformationCircleOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";

const Tooltip = ({ text, show, position = "right" }) => (
  show ? (
    <div className={`absolute ${position === "left" ? "right-0" : "left-1/2 -translate-x-1/2"} -top-4 -translate-y-full z-20 w-64 p-3 text-sm text-gray-700 bg-white rounded-lg shadow-xl border border-gray-100`}>
      {text}
      <div className={`absolute ${position === "left" ? "right-4" : "left-1/2 -translate-x-1/2"} bottom-[-6px] w-3 h-3 bg-white border-r border-b border-gray-100 transform rotate-45`}></div>
    </div>
  ) : null
);

const Cards = ({
    cards,
    analyticsLoading,
    analytics,
    isComparison = false,
    comparisonMode = false,
    filter2Data = null,
}) => {
  const [tooltipIndex, setTooltipIndex] = useState(null);

  const getPercentageChange = (value, name) => {
    if (!filter2Data || !comparisonMode) return null;
    const oldValue = filter2Data[name];
    const change = ((value / oldValue) - 1) * 100;
    return isFinite(change) ? change : 0;
  };

  const formatValue = (value, card) => {
    if (value === null || value === undefined) return '0';
    return card.valueFormatter ? card.valueFormatter(value) : value;
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 15,
      scale: 0.95
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 overflow-visible">
      <AnimatePresence>
        {analyticsLoading ? (
          <>
            {cards.map((card) => (
              <CardLoader key={card.name} />
            ))}
          </>
        ) : (
          <>
            {cards.map((card, index) => {
              const value1 = analytics?.[card.name] || 0;
              const value2 = filter2Data?.[card.name] || 0;
              const percentageChange = getPercentageChange(value1, card.name);
              const isPositive = percentageChange > 0;

              return (
                <motion.div
                  key={card.name}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300 relative overflow-visible group"
                  style={{
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(230,230,230,0.7)',
                    height: 'fit-content'
                  }}
                >
                  {/* Info Icon in Top-Right */}
                  {card.tooltip && (
                    <div
                      className="absolute top-4 right-4 z-10"
                      onMouseEnter={() => setTooltipIndex(index)}
                      onMouseLeave={() => setTooltipIndex(null)}
                      tabIndex={0}
                      onFocus={() => setTooltipIndex(index)}
                      onBlur={() => setTooltipIndex(null)}
                      aria-label="Show info"
                      role="button"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer">
                        <IoInformationCircleOutline className="w-5 h-5 text-gray-500" />
                      </div>
                      {/* Tooltip */}
                      <Tooltip 
                        text={card.tooltip} 
                        show={tooltipIndex === index} 
                        position={card.tooltipPosition}
                      />
                    </div>
                  )}
                  <div className="flex flex-col h-full justify-between">
                    <div className="flex justify-between items-start mb-4 w-4/5">
                      <span className="text-sm font-medium text-gray-500">
                        {card.displayName}
                      </span>
                    </div>

                    <div className="mt-2">
                      {comparisonMode ? (
                        <div className="text-2xl font-semibold text-gray-900">
                          {card.prefix || ""}
                          {formatValue(value1, card)}
                          {card.suffix || ""}
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                            {isComparison && (
                                <>
                                    <div className="text-xl font-semibold text-gray-900">
                                        {card.prefix || ""}
                                        {formatValue(value2, card)}
                                        {card.suffix || ""}
                                    </div>
                                    <div className={`${
                                      value1 > value2 
                                        ? 'text-green-500' 
                                        : value1 < value2 
                                          ? 'text-red-500' 
                                          : 'text-blue-500'
                                    }`}>
                                        <svg className="w-8 h-6 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                        </svg>
                                    </div>
                                </>
                            )}
                          <div className="text-xl font-semibold text-gray-900">
                            {card.prefix || ""}
                            {formatValue(value1, card)}
                            {card.suffix || ""}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="relative mt-4">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[95%] h-1">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ 
                          width: comparisonMode && percentageChange !== null
                            ? `${Math.min(Math.abs(percentageChange), 100)}%`
                            : '100%'
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className={`h-full rounded-full ${
                          comparisonMode && percentageChange !== null
                            ? isPositive 
                              ? 'bg-green-500'
                              : 'bg-red-500'
                            : 'bg-blue-500'
                        } opacity-20`}
                      />
                      </div>
                      {comparisonMode && percentageChange !== null && isComparison && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`absolute bottom-0 right-0 flex items-center ${
                            isPositive ? 'text-green-500 bg-green-50' : 'text-red-500 bg-red-50'
                          } text-sm font-medium px-2.5 py-1 rounded-full`}
                        >
                          {isPositive ? (
                            <IoTrendingUp className="w-3.5 h-3.5 mr-1" />
                          ) : (
                            <IoTrendingDown className="w-3.5 h-3.5 mr-1" />
                          )}
                          <span>{Math.abs(percentageChange).toFixed(1)}%</span>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cards;